import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import SchoolAvatar from '../components/SchoolAvatar';
import { apiFetch, apiFetchBlob, SOCKET_URL } from '../config/api';
import { isSuperAdminUser } from '../lib/adminAccess';

function normalizeParticipantType(type) {
  if (type === 'parent' || type === 'guardian' || type === 'driver') return 'contact';
  if (type === 'teacher') return 'staff';
  return type === 'staff' || type === 'contact' ? type : null;
}

function formatMessageTime(value) {
  if (!value) return 'No timestamp';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'No timestamp';

  return parsed.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function sortMessages(messages = []) {
  return [...messages].sort((left, right) => {
    const leftTime = new Date(left.sent_at || left.created_at || 0).getTime();
    const rightTime = new Date(right.sent_at || right.created_at || 0).getTime();
    if (leftTime !== rightTime) return leftTime - rightTime;
    return Number(left.id || 0) - Number(right.id || 0);
  });
}

function mergeMessage(messages, incomingMessage) {
  if (!incomingMessage?.id) return messages;

  return sortMessages([
    ...messages.filter((message) => Number(message.id) !== Number(incomingMessage.id)),
    incomingMessage,
  ]);
}

function isParentMessage(message) {
  return normalizeParticipantType(message?.sender_type) === 'contact';
}

function getConversationTime(conversation) {
  return formatMessageTime(conversation?.sent_at);
}

function isoDateForOffset(offsetDays = 0) {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}

function getStudentName(student) {
  return [student?.f_name, student?.l_name].filter(Boolean).join(' ').trim() || `Student #${student?.id}`;
}

function getWhatsappDisplayName(conversation) {
  return conversation?.contact_name
    || conversation?.profile_name
    || conversation?.display_name
    || conversation?.from_number
    || `WhatsApp #${conversation?.conversation_id || conversation?.id}`;
}

function getWhatsappSchoolsLabel(conversation) {
  const schools = conversation?.schools || [];
  if (!schools.length) return 'No school match';
  if (schools.length === 1) return schools[0].school_nickname || schools[0].legal_name || `School #${schools[0].id}`;
  return `${schools.length} matched schools`;
}

function getWhatsappMatchLabel(conversation) {
  if (conversation?.match_status === 'unmatched') return 'Unmatched';
  if (conversation?.match_status === 'ambiguous') return 'Multiple schools';
  return 'Matched';
}

function getWhatsappMeta(conversation, catchAll) {
  const pieces = [getWhatsappMatchLabel(conversation)];
  if (catchAll) pieces.push(getWhatsappSchoolsLabel(conversation));
  if (conversation?.from_number) pieces.push(conversation.from_number);
  return pieces.filter(Boolean).join(' · ');
}

function isInboundWhatsappMessage(message) {
  return message?.direction !== 'outbound';
}

function buildWhatsappConversationQuery({ catchAll, schoolId }) {
  const params = new URLSearchParams({ limit: '500' });
  if (!catchAll && schoolId) params.set('school_id', String(schoolId));
  return `/messages/whatsapp/conversations?${params.toString()}`;
}

function buildWhatsappThreadQuery({ conversationId, catchAll, schoolId }) {
  const params = new URLSearchParams({ limit: '500' });
  if (!catchAll && schoolId) params.set('school_id', String(schoolId));
  const query = params.toString();
  return `/messages/whatsapp/conversation/${conversationId}${query ? `?${query}` : ''}`;
}

function buildWhatsappMediaQuery({ conversationId, messageId, mediaIndex, catchAll, schoolId }) {
  const params = new URLSearchParams();
  if (!catchAll && schoolId) params.set('school_id', String(schoolId));
  const query = params.toString();
  return `/messages/whatsapp/conversation/${conversationId}/media/${messageId}/${mediaIndex}${query ? `?${query}` : ''}`;
}

function WhatsappMediaAttachment({
  conversationId,
  messageId,
  mediaIndex,
  contentType,
  catchAll,
  schoolId,
  token,
}) {
  const [mediaSource, setMediaSource] = useState(null);
  const [mediaError, setMediaError] = useState('');

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    setMediaSource(null);
    setMediaError('');

    apiFetchBlob(buildWhatsappMediaQuery({
      conversationId,
      messageId,
      mediaIndex,
      catchAll,
      schoolId,
    }), {}, token)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setMediaSource({
          url: objectUrl,
          contentType: blob.type || contentType || 'application/octet-stream',
        });
      })
      .catch((error) => {
        if (!cancelled) setMediaError(error.message || 'Media unavailable');
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [catchAll, contentType, conversationId, mediaIndex, messageId, schoolId, token]);

  if (mediaError) {
    return <span className="sam-message-media-error">Image unavailable</span>;
  }
  if (!mediaSource) {
    return <span className="sam-message-media-loading">Loading image…</span>;
  }

  const isImage = mediaSource.contentType.toLowerCase().startsWith('image/');
  if (!isImage) {
    return (
      <a className="sam-message-media-download" href={mediaSource.url} download>
        Open {mediaSource.contentType || 'media attachment'}
      </a>
    );
  }

  return (
    <a className="sam-message-image-link" href={mediaSource.url} target="_blank" rel="noreferrer">
      <img className="sam-message-image" src={mediaSource.url} alt="WhatsApp attachment" loading="lazy" />
    </a>
  );
}

export default function SchoolMessagesPage({ token, schoolId, user }) {
  const [activeTab, setActiveTab] = useState('app');
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const selectedConversationIdRef = useRef(selectedConversationId);
  selectedConversationIdRef.current = selectedConversationId;
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sendError, setSendError] = useState('');
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [whatsappConversations, setWhatsappConversations] = useState([]);
  const [selectedWhatsappConversationId, setSelectedWhatsappConversationId] = useState(null);
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappThreadLoading, setWhatsappThreadLoading] = useState(false);
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [whatsappError, setWhatsappError] = useState('');
  const [whatsappSendError, setWhatsappSendError] = useState('');
  const [whatsappDraft, setWhatsappDraft] = useState('');
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [assignmentResults, setAssignmentResults] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [assignmentError, setAssignmentError] = useState('');
  const [assignmentSuccess, setAssignmentSuccess] = useState('');
  const [assignmentStudent, setAssignmentStudent] = useState(null);
  const [assignmentStartDate, setAssignmentStartDate] = useState(isoDateForOffset(0));
  const [assignmentEndDate, setAssignmentEndDate] = useState(isoDateForOffset(1));

  const whatsappCatchAll = isSuperAdminUser(user);

  const loadConversations = useCallback(async () => {
    if (!schoolId || !token) return;

    setError('');
    setLoading(true);

    try {
      const res = await apiFetch(`/messages/school/${schoolId}/conversations?limit=500`, {}, token);
      const rows = res.data || [];
      setConversations((current) => {
        const selectedId = selectedConversationIdRef.current;
        if (!selectedId || rows.some((conversation) => Number(conversation.conversation_id) === Number(selectedId))) {
          return rows;
        }
        const kept = current.find((conversation) => (
          Number(conversation.conversation_id) === Number(selectedId)
          && Number(conversation.school_id) === Number(schoolId)
        ));
        return kept ? [kept, ...rows] : rows;
      });
    } catch (err) {
      setError(err.message || 'Unable to load school messages.');
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, token]);

  const loadWhatsappConversations = useCallback(async () => {
    if (!token || (!whatsappCatchAll && !schoolId)) return;

    setWhatsappError('');
    setWhatsappLoading(true);

    try {
      const res = await apiFetch(buildWhatsappConversationQuery({
        catchAll: whatsappCatchAll,
        schoolId,
      }), {}, token);
      setWhatsappConversations(res.data || []);
    } catch (err) {
      setWhatsappError(err.message || 'Unable to load WhatsApp conversations.');
      setWhatsappConversations([]);
    } finally {
      setWhatsappLoading(false);
    }
  }, [schoolId, token, whatsappCatchAll]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (activeTab === 'whatsapp') {
      loadWhatsappConversations();
    }
  }, [activeTab, loadWhatsappConversations]);

  const selectedConversation = useMemo(
    () => conversations.find((conversation) => Number(conversation.conversation_id) === Number(selectedConversationId)) || null,
    [conversations, selectedConversationId]
  );

  const selectedWhatsappConversation = useMemo(
    () => whatsappConversations.find((conversation) => Number(conversation.conversation_id) === Number(selectedWhatsappConversationId)) || null,
    [selectedWhatsappConversationId, whatsappConversations]
  );

  useEffect(() => {
    if (!conversations.length) {
      setSelectedConversationId(null);
      return;
    }

    const selectedStillExists = conversations.some(
      (conversation) => Number(conversation.conversation_id) === Number(selectedConversationId)
    );

    if (!selectedStillExists) {
      setSelectedConversationId(conversations[0].conversation_id);
    }
  }, [conversations, selectedConversationId]);

  useEffect(() => {
    if (!whatsappConversations.length) {
      setSelectedWhatsappConversationId(null);
      return;
    }

    const selectedStillExists = whatsappConversations.some(
      (conversation) => Number(conversation.conversation_id) === Number(selectedWhatsappConversationId)
    );

    if (!selectedStillExists) {
      setSelectedWhatsappConversationId(whatsappConversations[0].conversation_id);
    }
  }, [selectedWhatsappConversationId, whatsappConversations]);

  useEffect(() => {
    if (!selectedConversation?.conversation_id) {
      setMessages([]);
      return undefined;
    }

    let cancelled = false;
    setThreadLoading(true);
    setSendError('');

    apiFetch(`/messages/school/${schoolId}/conversation/${selectedConversation.conversation_id}`, {}, token)
      .then((res) => {
        if (!cancelled) setMessages(sortMessages(res.data || []));
      })
      .catch((err) => {
        if (!cancelled) {
          setMessages([]);
          setSendError(err.message || 'Unable to load this conversation.');
        }
      })
      .finally(() => {
        if (!cancelled) setThreadLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId, selectedConversation?.conversation_id, token]);

  useEffect(() => {
    if (!selectedWhatsappConversation?.conversation_id || activeTab !== 'whatsapp') {
      setWhatsappMessages([]);
      return undefined;
    }

    let cancelled = false;
    setWhatsappThreadLoading(true);
    setWhatsappSendError('');

    apiFetch(buildWhatsappThreadQuery({
      conversationId: selectedWhatsappConversation.conversation_id,
      catchAll: whatsappCatchAll,
      schoolId,
    }), {}, token)
      .then((res) => {
        if (!cancelled) setWhatsappMessages(sortMessages(res.data || []));
      })
      .catch((err) => {
        if (!cancelled) {
          setWhatsappMessages([]);
          setWhatsappSendError(err.message || 'Unable to load this WhatsApp conversation.');
        }
      })
      .finally(() => {
        if (!cancelled) setWhatsappThreadLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, schoolId, selectedWhatsappConversation?.conversation_id, token, whatsappCatchAll]);

  useEffect(() => {
    if (!selectedConversation?.conversation_id || activeTab !== 'app') return undefined;

    const socket = io(SOCKET_URL);
    const conversationId = Number(selectedConversation.conversation_id);

    const watchConversation = () => {
      socket.emit('watch-conversation', conversationId);
    };

    socket.on('connect', watchConversation);
    socket.on('message-created', (payload = {}) => {
      if (Number(payload.conversation_id) !== conversationId) return;

      setMessages((current) => mergeMessage(current, payload.message));
      loadConversations();
    });

    return () => socket.disconnect();
  }, [activeTab, loadConversations, selectedConversation?.conversation_id]);

  useEffect(() => {
    if (activeTab !== 'whatsapp') return undefined;

    const socket = io(SOCKET_URL);
    const conversationId = selectedWhatsappConversation?.conversation_id
      ? Number(selectedWhatsappConversation.conversation_id)
      : null;

    const watchWhatsapp = () => {
      if (whatsappCatchAll) socket.emit('watch-whatsapp-catch-all');
      if (!whatsappCatchAll && schoolId) socket.emit('watch-school', schoolId);
      if (conversationId) socket.emit('watch-whatsapp-conversation', conversationId);
    };

    socket.on('connect', watchWhatsapp);
    socket.on('whatsapp-message-created', (payload = {}) => {
      if (conversationId && Number(payload.conversation_id) === conversationId) {
        setWhatsappMessages((current) => mergeMessage(current, payload.message));
      }
      loadWhatsappConversations();
    });

    return () => socket.disconnect();
  }, [
    activeTab,
    loadWhatsappConversations,
    schoolId,
    selectedWhatsappConversation?.conversation_id,
    whatsappCatchAll,
  ]);

  const filteredConversations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return conversations;

    return conversations.filter((conversation) => [
      conversation.peer_name,
      conversation.staff_name,
      conversation.body,
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [conversations, search]);

  const filteredWhatsappConversations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return whatsappConversations;

    return whatsappConversations.filter((conversation) => [
      getWhatsappDisplayName(conversation),
      conversation.from_number,
      conversation.to_number,
      conversation.profile_name,
      conversation.body,
      conversation.match_status,
      ...(conversation.contacts || []).flatMap((contact) => [contact.name, contact.email, contact.phone]),
      ...(conversation.schools || []).flatMap((school) => [school.legal_name, school.school_nickname]),
    ].some((value) => String(value || '').toLowerCase().includes(query)));
  }, [search, whatsappConversations]);

  const handleSend = async () => {
    const body = draft.trim();
    if (!body || !selectedConversation || sending) return;

    setSending(true);
    setSendError('');
    setConversations((current) => current.map((conversation) => (
      Number(conversation.conversation_id) === Number(selectedConversation.conversation_id)
        ? {
            ...conversation,
            body,
            sent_at: new Date().toISOString(),
            sender_id: user.id,
            sender_type: 'staff',
            status: 'sent',
            staff_id: user.id,
            staff_name: [user.f_name, user.l_name].filter(Boolean).join(' ') || conversation.staff_name,
          }
        : conversation
    )));

    try {
      const res = await apiFetch('/messages', {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          conversation_id: selectedConversation.conversation_id,
          sender_id: user.id,
          sender_type: 'staff',
          recipient_id: selectedConversation.peer_id,
          recipient_type: 'contact',
          body,
          status: 'sent',
          sent_at: new Date().toISOString(),
        }),
      }, token);

      setDraft('');
      if (res.data) setMessages((current) => mergeMessage(current, res.data));
      await loadConversations();
    } catch (err) {
      setSendError(err.message || 'Unable to send reply.');
    } finally {
      setSending(false);
    }
  };

  const handleWhatsappSend = async () => {
    const body = whatsappDraft.trim();
    if (!body || !selectedWhatsappConversation || whatsappSending) return;

    setWhatsappSending(true);
    setWhatsappSendError('');

    try {
      const res = await apiFetch(`/messages/whatsapp/conversation/${selectedWhatsappConversation.conversation_id}/reply`, {
        method: 'POST',
        body: JSON.stringify({
          body,
          school_id: whatsappCatchAll ? undefined : schoolId,
        }),
      }, token);

      setWhatsappDraft('');
      if (res.data) setWhatsappMessages((current) => mergeMessage(current, res.data));
      await loadWhatsappConversations();
    } catch (err) {
      setWhatsappSendError(err.message || 'Unable to send WhatsApp reply.');
    } finally {
      setWhatsappSending(false);
    }
  };

  const needsReply = selectedConversation && isParentMessage(selectedConversation);
  const selectedContactName = selectedConversation?.peer_name || `Contact #${selectedConversation?.peer_id}`;
  const canAssignStudent = Boolean(selectedConversation?.peer_id);
  const whatsappNeedsReply = selectedWhatsappConversation?.last_direction === 'inbound';
  const activeError = activeTab === 'whatsapp' ? whatsappError : error;
  const activeLoading = activeTab === 'whatsapp' ? whatsappLoading : loading;

  useEffect(() => {
    setAssignmentOpen(false);
    setAssignmentSearch('');
    setAssignmentResults([]);
    setAssignmentStudent(null);
    setAssignmentError('');
    setAssignmentSuccess('');
    setAssignmentStartDate(isoDateForOffset(0));
    setAssignmentEndDate(isoDateForOffset(1));
  }, [selectedConversation?.conversation_id]);

  useEffect(() => {
    if (!assignmentOpen || !token || !schoolId) return undefined;

    const query = assignmentSearch.trim();
    if (query.length > 0 && query.length < 2) {
      setAssignmentResults([]);
      setAssignmentLoading(false);
      return undefined;
    }

    let cancelled = false;
    setAssignmentLoading(true);
    setAssignmentError('');

    const timeoutId = setTimeout(() => {
      const path = query
        ? `/students/search?school_id=${schoolId}&name=${encodeURIComponent(query)}&status=active&limit=12&orderBy=l_name&orderDirection=ASC`
        : `/students?school_id=${schoolId}&status=active&limit=12&orderBy=l_name&orderDirection=ASC`;

      apiFetch(path, {}, token)
        .then((res) => {
          if (!cancelled) setAssignmentResults(res.data || []);
        })
        .catch((err) => {
          if (!cancelled) {
            setAssignmentResults([]);
            setAssignmentError(err.message || 'Unable to search students.');
          }
        })
        .finally(() => {
          if (!cancelled) setAssignmentLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [assignmentOpen, assignmentSearch, schoolId, token]);

  const openAssignment = () => {
    setAssignmentOpen(true);
    setAssignmentSearch('');
    setAssignmentResults([]);
    setAssignmentStudent(null);
    setAssignmentError('');
    setAssignmentSuccess('');
    setAssignmentStartDate(isoDateForOffset(0));
    setAssignmentEndDate(isoDateForOffset(1));
  };

  const closeAssignment = () => {
    if (assignmentSaving) return;
    setAssignmentOpen(false);
  };

  const handleAssignStudent = async () => {
    if (!selectedConversation?.peer_id || !assignmentStudent?.id) {
      setAssignmentError('Select a student before saving.');
      return;
    }

    if (!assignmentStartDate || !assignmentEndDate || new Date(assignmentEndDate) <= new Date(assignmentStartDate)) {
      setAssignmentError('End date must be after the start date.');
      return;
    }

    setAssignmentSaving(true);
    setAssignmentError('');
    setAssignmentSuccess('');

    try {
      await apiFetch('/temporary-authorized-drivers', {
        method: 'POST',
        body: JSON.stringify({
          school_id: schoolId,
          student_id: assignmentStudent.id,
          authorizing_contact_id: selectedConversation.peer_id,
          authorized_contact_id: selectedConversation.peer_id,
          start_date: assignmentStartDate,
          end_date: assignmentEndDate,
          status: 'accepted',
          notes: `Assigned from admin message thread ${selectedConversation.conversation_id}`,
        }),
      }, token);

      setAssignmentSuccess(`${getStudentName(assignmentStudent)} assigned to ${selectedContactName}.`);
      setAssignmentStudent(null);
      setAssignmentSearch('');
      setAssignmentResults([]);
    } catch (err) {
      setAssignmentError(err.message || 'Unable to assign student.');
    } finally {
      setAssignmentSaving(false);
    }
  };

  const renderAppInbox = () => (
    <div className="sam-shell">
      <aside className="sam-inbox">
        <div className="sam-search">
          <span aria-hidden="true">⌕</span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search messages"
          />
        </div>

        <div className="sam-inbox-list">
          {loading ? <div className="sam-empty">Loading conversations...</div> : null}
          {!loading && !filteredConversations.length ? (
            <div className="sam-empty">No school messages found.</div>
          ) : null}

          {filteredConversations.map((conversation) => {
            const active = Number(conversation.conversation_id) === Number(selectedConversationId);
            const parentLatest = isParentMessage(conversation);

            return (
              <button
                key={conversation.conversation_id}
                type="button"
                className={`sam-conversation-row ${active ? 'active' : ''}`}
                onClick={() => setSelectedConversationId(conversation.conversation_id)}
              >
                <SchoolAvatar
                  name={conversation.peer_name}
                  seed={conversation.peer_id}
                  photoPath={conversation.peer_photo_relative_path}
                  className="sam-avatar"
                />
                <span className="sam-conversation-copy">
                  <span className="sam-conversation-top">
                    <strong>{conversation.peer_name || `Parent #${conversation.peer_id}`}</strong>
                    <span>{getConversationTime(conversation)}</span>
                  </span>
                  <span className="sam-conversation-meta">
                    {parentLatest ? 'Parent message' : `Replied by ${conversation.staff_name || 'school staff'}`}
                  </span>
                  <span className="sam-conversation-preview">{conversation.body}</span>
                </span>
                {parentLatest ? <span className="sam-reply-dot" title="Needs reply" /> : null}
              </button>
            );
          })}
        </div>
      </aside>

      <section className="sam-thread">
        {selectedConversation ? (
          <>
            <div className="sam-thread-head">
              <div className="sam-thread-parent">
                <SchoolAvatar
                  name={selectedConversation.peer_name}
                  seed={selectedConversation.peer_id}
                  photoPath={selectedConversation.peer_photo_relative_path}
                  className="sam-avatar sam-avatar-large"
                />
                <div>
                  <h3>{selectedConversation.peer_name || `Parent #${selectedConversation.peer_id}`}</h3>
                  <p>
                    Conversation with {selectedConversation.staff_name || 'school staff'}
                  </p>
                </div>
              </div>
              <div className="sam-thread-actions">
                <button
                  type="button"
                  className="sam-assign"
                  onClick={openAssignment}
                  disabled={!canAssignStudent}
                >
                  Assign student
                </button>
                <span className={`sam-status ${needsReply ? 'attention' : ''}`}>
                  {needsReply ? 'Needs reply' : 'Replied'}
                </span>
              </div>
            </div>

            <div className="sam-thread-body">
              {threadLoading ? <div className="sam-empty">Loading thread...</div> : null}
              {!threadLoading && !messages.length ? (
                <div className="sam-empty">No messages in this thread.</div>
              ) : null}

              {messages.map((message) => {
                const parentSide = isParentMessage(message);

                return (
                  <div key={message.id} className={`sam-message-row ${parentSide ? 'parent' : 'school'}`}>
                    <div className="sam-message-bubble">
                      <p>{message.body}</p>
                      <span>{formatMessageTime(message.sent_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sam-reply-box">
              {sendError ? <div className="sam-send-error">{sendError}</div> : null}
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={`Reply to ${selectedConversation.peer_name || 'parent'}`}
                rows={3}
              />
              <div className="sam-reply-actions">
                <span>Sending as {user.name || user.email || 'school admin'}</span>
                <button
                  type="button"
                  className="sam-send"
                  onClick={handleSend}
                  disabled={!draft.trim() || sending}
                >
                  {sending ? 'Sending...' : 'Send reply'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="sam-thread-empty">
            <h3>No conversation selected</h3>
            <p>Select a parent message to read the thread and reply.</p>
          </div>
        )}
      </section>
    </div>
  );

  const renderWhatsappInbox = () => (
    <div className="sam-shell sam-shell-whatsapp">
      <aside className="sam-inbox">
        <div className="sam-search">
          <span aria-hidden="true">⌕</span>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search WhatsApp"
          />
        </div>

        <div className="sam-inbox-list">
          {whatsappLoading ? <div className="sam-empty">Loading WhatsApp conversations...</div> : null}
          {!whatsappLoading && !filteredWhatsappConversations.length ? (
            <div className="sam-empty">No WhatsApp conversations found.</div>
          ) : null}

          {filteredWhatsappConversations.map((conversation) => {
            const active = Number(conversation.conversation_id) === Number(selectedWhatsappConversationId);
            const displayName = getWhatsappDisplayName(conversation);
            const inboundLatest = conversation.last_direction !== 'outbound';

            return (
              <button
                key={conversation.conversation_id}
                type="button"
                className={`sam-conversation-row ${active ? 'active' : ''}`}
                onClick={() => setSelectedWhatsappConversationId(conversation.conversation_id)}
              >
                <SchoolAvatar
                  name={displayName}
                  seed={conversation.conversation_id}
                  photoPath={conversation.contact?.photo_relative_path}
                  className="sam-avatar"
                />
                <span className="sam-conversation-copy">
                  <span className="sam-conversation-top">
                    <strong>{displayName}</strong>
                    <span>{getConversationTime(conversation)}</span>
                  </span>
                  <span className="sam-conversation-meta">
                    <span className="sam-channel-chip whatsapp">WhatsApp</span>
                    {getWhatsappMeta(conversation, whatsappCatchAll)}
                  </span>
                  <span className="sam-conversation-preview">{conversation.body || 'No message body'}</span>
                </span>
                {inboundLatest ? <span className="sam-reply-dot" title="Needs reply" /> : null}
              </button>
            );
          })}
        </div>
      </aside>

      <section className="sam-thread">
        {selectedWhatsappConversation ? (
          <>
            <div className="sam-thread-head">
              <div className="sam-thread-parent">
                <SchoolAvatar
                  name={getWhatsappDisplayName(selectedWhatsappConversation)}
                  seed={selectedWhatsappConversation.conversation_id}
                  photoPath={selectedWhatsappConversation.contact?.photo_relative_path}
                  className="sam-avatar sam-avatar-large"
                />
                <div>
                  <h3>{getWhatsappDisplayName(selectedWhatsappConversation)}</h3>
                  <p>{selectedWhatsappConversation.from_number || 'Unknown WhatsApp number'}</p>
                </div>
              </div>
              <div className="sam-thread-actions">
                <span className="sam-status whatsapp">WhatsApp</span>
                <span className={`sam-status ${whatsappNeedsReply ? 'attention' : ''}`}>
                  {whatsappNeedsReply ? 'Needs reply' : 'Replied'}
                </span>
              </div>
            </div>

            <div className="sam-whatsapp-context">
              <div>
                <span>Contact</span>
                <strong>{selectedWhatsappConversation.contact_name || 'No matching contact'}</strong>
                <small>
                  {selectedWhatsappConversation.contact?.email
                    || selectedWhatsappConversation.contact?.phone
                    || selectedWhatsappConversation.profile_name
                    || 'Unknown sender'}
                </small>
              </div>
              <div>
                <span>Schools</span>
                <strong>{getWhatsappSchoolsLabel(selectedWhatsappConversation)}</strong>
                <small>
                  {(selectedWhatsappConversation.schools || [])
                    .map((school) => school.school_nickname || school.legal_name || `School #${school.id}`)
                    .join(', ') || 'Available only in super admin catch-all'}
                </small>
              </div>
              <div>
                <span>WhatsApp</span>
                <strong>{getWhatsappMatchLabel(selectedWhatsappConversation)}</strong>
                <small>
                  From {selectedWhatsappConversation.from_number || 'unknown'} to {selectedWhatsappConversation.to_number || 'unknown'}
                </small>
              </div>
            </div>

            <div className="sam-thread-body">
              {whatsappThreadLoading ? <div className="sam-empty">Loading WhatsApp thread...</div> : null}
              {!whatsappThreadLoading && !whatsappMessages.length ? (
                <div className="sam-empty">No WhatsApp messages in this thread.</div>
              ) : null}

              {whatsappMessages.map((message) => {
                const inbound = isInboundWhatsappMessage(message);
                const media = Array.isArray(message.media_json) ? message.media_json : [];
                const messageBody = String(message?.body || '').trim();

                return (
                  <div key={message.id} className={`sam-message-row ${inbound ? 'parent' : 'school'}`}>
                    <div className="sam-message-bubble">
                      {messageBody ? <p>{messageBody}</p> : null}
                      {media.length ? (
                        <div className="sam-message-media">
                          {media.map((item, index) => (
                            <WhatsappMediaAttachment
                              key={`${message.id}-media-${index}`}
                              conversationId={selectedWhatsappConversation.conversation_id}
                              messageId={message.id}
                              mediaIndex={index}
                              contentType={item.content_type}
                              catchAll={whatsappCatchAll}
                              schoolId={schoolId}
                              token={token}
                            />
                          ))}
                        </div>
                      ) : null}
                      <span>
                        {formatMessageTime(message.sent_at || message.created_at)}
                        {message.message_status ? ` · ${message.message_status}` : ''}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="sam-reply-box">
              {whatsappSendError ? <div className="sam-send-error">{whatsappSendError}</div> : null}
              <textarea
                value={whatsappDraft}
                onChange={(event) => setWhatsappDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    handleWhatsappSend();
                  }
                }}
                placeholder={`Reply on WhatsApp to ${getWhatsappDisplayName(selectedWhatsappConversation)}`}
                rows={3}
              />
              <div className="sam-reply-actions">
                <span>
                  {whatsappCatchAll ? 'Sending from the catch-all inbox' : `Sending as ${user.name || user.email || 'school admin'}`}
                </span>
                <button
                  type="button"
                  className="sam-send"
                  onClick={handleWhatsappSend}
                  disabled={!whatsappDraft.trim() || whatsappSending}
                >
                  {whatsappSending ? 'Sending...' : 'Send WhatsApp'}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="sam-thread-empty">
            <h3>No WhatsApp conversation selected</h3>
            <p>Select a WhatsApp reply to review sender and school matches.</p>
          </div>
        )}
      </section>
    </div>
  );

  return (
    <div className="sam-page">
      <section className="sam-header">
        <div>
          <h2>Messages</h2>
          <p>
            {activeTab === 'whatsapp'
              ? (whatsappCatchAll
                ? 'Catch all inbound WhatsApp replies and see contact and school matches.'
                : 'Review WhatsApp replies matched to this school.')
              : 'Review parent messages sent to the school and reply from the admin desk.'}
          </p>
        </div>
        <button
          type="button"
          className="sam-refresh"
          onClick={activeTab === 'whatsapp' ? loadWhatsappConversations : loadConversations}
          disabled={activeLoading}
        >
          Refresh
        </button>
      </section>

      <div className="sam-tabs" role="tablist" aria-label="Message channels">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'app'}
          className={activeTab === 'app' ? 'active' : ''}
          onClick={() => setActiveTab('app')}
        >
          School app
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'whatsapp'}
          className={activeTab === 'whatsapp' ? 'active' : ''}
          onClick={() => setActiveTab('whatsapp')}
        >
          WhatsApp
        </button>
      </div>

      {activeError ? <div className="sam-error">{activeError}</div> : null}

      {activeTab === 'whatsapp' ? renderWhatsappInbox() : renderAppInbox()}

      {assignmentOpen && selectedConversation && activeTab === 'app' ? (
        <div className="sam-assignment-modal-backdrop" onClick={closeAssignment}>
          <div className="sam-assignment-modal" onClick={(event) => event.stopPropagation()}>
            <div className="sam-assignment-head">
              <div>
                <h3>Assign Student</h3>
                <p>{selectedContactName}</p>
              </div>
              <button type="button" onClick={closeAssignment} aria-label="Close assignment modal">x</button>
            </div>

            {assignmentError ? <div className="sam-send-error">{assignmentError}</div> : null}
            {assignmentSuccess ? <div className="sam-assignment-success">{assignmentSuccess}</div> : null}

            <label className="sam-assignment-field">
              <span>Student</span>
              <input
                value={assignmentSearch}
                onChange={(event) => {
                  setAssignmentSearch(event.target.value);
                  setAssignmentStudent(null);
                  setAssignmentSuccess('');
                }}
                placeholder="Search by name"
              />
            </label>

            <div className="sam-assignment-results">
              {assignmentLoading ? <div className="sam-empty">Searching students...</div> : null}
              {!assignmentLoading && !assignmentResults.length ? (
                <div className="sam-empty">No students found.</div>
              ) : null}
              {assignmentResults.map((student) => {
                const selected = Number(assignmentStudent?.id) === Number(student.id);
                return (
                  <button
                    key={student.id}
                    type="button"
                    className={`sam-assignment-student ${selected ? 'selected' : ''}`}
                    onClick={() => {
                      setAssignmentStudent(student);
                      setAssignmentSuccess('');
                    }}
                  >
                    <SchoolAvatar name={getStudentName(student)} seed={student.id} photoPath={student.photo_relative_path} className="sam-avatar" />
                    <span>
                      <strong>{getStudentName(student)}</strong>
                      <small>Grade {student.grade}</small>
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="sam-assignment-dates">
              <label className="sam-assignment-field">
                <span>Start</span>
                <input type="date" value={assignmentStartDate} onChange={(event) => setAssignmentStartDate(event.target.value)} />
              </label>
              <label className="sam-assignment-field">
                <span>End</span>
                <input type="date" value={assignmentEndDate} onChange={(event) => setAssignmentEndDate(event.target.value)} />
              </label>
            </div>

            <div className="sam-assignment-actions">
              <button type="button" className="sam-refresh" onClick={closeAssignment} disabled={assignmentSaving}>
                Cancel
              </button>
              <button
                type="button"
                className="sam-send"
                onClick={handleAssignStudent}
                disabled={assignmentSaving || !assignmentStudent}
              >
                {assignmentSaving ? 'Saving...' : 'Save assignment'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
